/**
 * Learner-facing language availability (Multi-Language Trainings,
 * phase 3). Availability is a SWITCH, not a computation: a language is
 * available to end users ⟺ it was ADDED to the training AND its "Visible to
 * users" (`active`) toggle is ON. Translation completeness never matters.
 *
 * This is THE shared derivation — the course-overview language selector (later
 * workstream) and every learner-facing guard consume these helpers. The
 * interfaces are structural (no model imports) so the runner translation
 * service can use them without dragging the datastore import graph into
 * `course-activity.model.ts` (module cycle).
 */

// The `language`/`active` slice of a TrainingTranslation record.
export interface TrainingTranslationActivation {
  readonly language: string;
  // Older payloads may not carry the attribute — undefined counts as active.
  readonly active?: boolean | null;
}

// The training slice needed to derive availability. Course, ILTCourse and
// LearningPath all conform structurally. `trainingTranslations` must be
// INCLUDED in the payload wherever an OFF language has to be invisible —
// without the rows every added language counts as active (the default).
export interface TrainingLanguageAvailability {
  // Source (authoring) language — always available.
  readonly language: string;
  // Added translation languages (source excluded).
  readonly translationLanguages?: readonly string[] | null;
  readonly trainingTranslations?:
    | readonly TrainingTranslationActivation[]
    | null;
}

// Whether an ADDED language's visibility toggle is on. A language without a
// persisted row (nothing translated yet, toggle untouched) defaults to active.
function isTranslationActive(
  training: TrainingLanguageAvailability,
  language: string,
): boolean {
  const record = (training.trainingTranslations ?? []).find(
    translation => translation.language === language,
  );
  return record ? record.active !== false : true;
}

/**
 * The languages a LEARNER may consume: the source language plus every added
 * translation whose `active` toggle is on, in the training's language order.
 */
export function learnerAvailableLanguages(
  training: TrainingLanguageAvailability,
): string[] {
  const added = training.translationLanguages ?? [];
  return [
    training.language,
    ...added.filter(language => isTranslationActive(training, language)),
  ];
}

/** Whether `language` is available to learners (source is always available). */
export function isLanguageAvailableToLearners(
  training: TrainingLanguageAvailability,
  language: string,
): boolean {
  if (language === training.language) {
    return true;
  }
  return (
    (training.translationLanguages ?? []).includes(language) &&
    isTranslationActive(training, language)
  );
}

/**
 * The language a learner actually gets: the preferred (profile/UI) language
 * when it is available, else the source. An OFF language therefore behaves,
 * for learners, exactly as if it were never added.
 */
export function resolveLearnerLanguage(
  training: TrainingLanguageAvailability,
  preferredLanguage: string,
): string {
  return isLanguageAvailableToLearners(training, preferredLanguage)
    ? preferredLanguage
    : training.language;
}
